/**
 * DeptReportDoc — the printable department report document (Claude Design
 * "Reports" handoff).
 *
 * One component for both documents; `mode` picks the only structural
 * difference — weekly groups rows by service, daily groups them by vehicle.
 *
 * Rows are the existing billing-line shape from ReportsPanel:
 *   { date, week, stockNumber, model, units, service, comments, total, gst }
 */

import React from 'react';
import { ACTS_COMPANY } from '../config/company';
import {
  INK, YELLOW, GROUP_TINT, TEAL, NAVY, ORANGE, DAY_TINT,
  T1, T3, T5, MONO, NUM, BRAND_LOGO, DEPT_MANAGER,
  DAYS, money, parseISO, dmy, longDate, isoWeekRange, titleComment, plural,
  th, td, MetaCell, Masthead, GroupBar, TotalsStack, DocFooter,
} from './reportKit';

export default function DeptReportDoc({ mode, dept, rows, week, year, date }) {
  const weekly = mode === 'weekly';
  const sub = rows.reduce((s, r) => s + (Number(r.total) || 0), 0);
  const gst = rows.reduce((s, r) => s + (Number(r.gst) || 0), 0);
  const units = rows.reduce((s, r) => s + (parseFloat(r.units) || 1), 0);

  // Group: weekly by service (highest value first), daily by vehicle.
  const groups = [];
  const byKey = new Map();
  for (const r of rows) {
    const key = weekly ? r.service : (r.stockNumber || '—');
    if (!byKey.has(key)) {
      const g = { key, rows: [], total: 0, units: 0, model: r.model };
      byKey.set(key, g); groups.push(g);
    }
    const g = byKey.get(key);
    g.rows.push(r); g.total += Number(r.total) || 0; g.units += parseFloat(r.units) || 1;
  }
  if (weekly) groups.sort((a, b) => b.total - a.total);
  else groups.sort((a, b) => String(a.key).localeCompare(String(b.key)));

  const svcCounts = {};
  rows.forEach(r => { svcCounts[r.service] = (svcCounts[r.service] || 0) + (parseFloat(r.units) || 1); });

  const deptShort = dept.replace(/\s*CARS$/, '');
  const kicker = weekly ? 'Weekly Summary' : 'Daily Paperwork';
  const issued = weekly ? new Date() : parseISO(date);
  const period = weekly
    ? `Week ${week} · ${isoWeekRange(week, year)}`
    : `${DAYS[parseISO(date).getDay()]} ${longDate(parseISO(date))} · Week ${rows[0]?.week || ''}`;
  const ref = weekly
    ? `REF ACTS-W${week}-${deptShort.replace(/\s+/g, '')}-${year}`
    : `REF ACTS-D${dmy(date).replace(/\//g, '').slice(0, 6)}-${deptShort.replace(/\s+/g, '')}`;
  const hasPartial = rows.some(r => (parseFloat(r.units) || 1) < 1);

  return (
    <div className="deptdoc" style={{ background: '#fff', color: T1, padding: '38px 40px 30px', display: 'flex', flexDirection: 'column' }}>

      <Masthead
        kicker={kicker}
        subject={dept}
        seal={deptShort}
        logo={BRAND_LOGO[dept]}
        period={period}
        totalLabel="Total incl. GST"
        total={sub + gst}
        reference={ref}
      />

      <div style={{ display: 'flex', borderTop: `3px solid ${INK}`, borderBottom: '1px solid #dcdcdc', padding: '10px 0' }}>
        <MetaCell first label="From" value={ACTS_COMPANY.name} sub={`ABN ${ACTS_COMPANY.abn} · ${ACTS_COMPANY.address}`} />
        <MetaCell label="Attention" value={DEPT_MANAGER[dept] || `${deptShort} Manager`}
          sub={`${deptShort} · Melville Motors`} />
        <MetaCell label={weekly ? 'Issued' : 'Submitted'} value={longDate(issued)}
          sub={weekly ? 'Queries within 7 days' : 'Same-day paperwork'} />
        <MetaCell last label="Counts"
          value={`${plural(rows.length, 'job')} · ${Math.round(units * 100) / 100} units`}
          sub={plural(Object.keys(svcCounts).length, 'service type')} />
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
        <thead>
          <tr>
            <th style={{ ...th, width: 66 }}>Date</th>
            {weekly && <th style={{ ...th, width: 84 }}>Stock</th>}
            {weekly && <th style={th}>Model</th>}
            {!weekly && <th className="svc-cell" style={th}>Job description</th>}
            <th style={th}>Comments</th>
            <th style={{ ...th, width: 44, textAlign: 'right' }}>Unit</th>
            <th style={{ ...th, width: 74, textAlign: 'right' }}>Amount</th>
            <th style={{ ...th, width: 64, textAlign: 'right', color: T5 }}>GST</th>
          </tr>
        </thead>
        <tbody>
          {groups.map(g => (
            <React.Fragment key={g.key}>
              <GroupBar accent={weekly ? YELLOW : TEAL} tint={weekly ? GROUP_TINT : DAY_TINT}>
                {weekly ? (
                  <>
                    <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2, color: INK }}>{g.key}</span>
                    <span style={{ fontSize: 9.5, fontWeight: 600, color: '#8a7420', ...NUM }}>
                      {plural(g.rows.length, 'job')} · {Math.round(g.units * 100) / 100} units
                    </span>
                  </>
                ) : (
                  <>
                    <span style={{ fontFamily: MONO, fontSize: 10.5, fontWeight: 800, color: INK, letterSpacing: 0.5 }}>{g.key}</span>
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: NAVY }}>{g.model}</span>
                    {g.rows.length > 1 && <span style={{ fontSize: 9.5, fontWeight: 600, color: '#6b7280' }}>{plural(g.rows.length, 'job')}</span>}
                  </>
                )}
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 11, fontWeight: 800, color: INK, ...NUM }}>{money(g.total)}</span>
              </GroupBar>

              {g.rows.map((r, i) => {
                const u = parseFloat(r.units) || 1;
                const c = titleComment(r.comments);
                return (
                  <tr key={i}>
                    <td style={{ ...td, ...NUM }}>{dmy(r.date)}</td>
                    {weekly && <td style={{ ...td, fontFamily: MONO, fontWeight: 700, letterSpacing: 0.5 }}>{r.stockNumber}</td>}
                    {weekly && <td style={{ ...td, fontWeight: 600 }}>{r.model}</td>}
                    {!weekly && <td className="svc-cell" style={{ ...td, whiteSpace: 'nowrap' }}>{r.service}</td>}
                    <td style={{ ...td, color: c ? T3 : '#c3ccd3' }}>{c || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', ...NUM, ...(u < 1 ? { color: ORANGE, fontWeight: 700 } : {}) }}>{u.toFixed(2)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, ...NUM }}>{money(r.total)}</td>
                    <td style={{ ...td, textAlign: 'right', color: T5, ...NUM }}>{money(r.gst)}</td>
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      <TotalsStack
        lines={[{ label: 'Subtotal (ex. GST)', value: sub }, { label: 'GST 10%', value: gst }]}
        grandLabel="TOTAL + GST"
        grandValue={sub + gst}
      />

      <div style={{ flex: 1 }} />

      <DocFooter
        counts={svcCounts}
        docName={kicker}
        right={`${dept} · ${weekly ? `W${week} ${year}` : dmy(date)}`}
        notes={weekly ? (
          <>
            {hasPartial && 'Partial units (shown in orange) are jobs split across vehicles or scopes. '}
            Buffing is invoiced only against an authorised request. Please raise any query within 7 days of the issue date.
          </>
        ) : (
          <>
            Jobs are grouped per vehicle with a subtotal for each car.
            {hasPartial && ' Partial units (shown in orange) are split-scope jobs.'}
            {' '}This paperwork is submitted the same day the work is completed.
          </>
        )}
      />
    </div>
  );
}
